/**
 * @file Product Tour
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Product Tour component
 */
const vProductTour = () => {
	const productTour = document.querySelector( '.v-product-tour' );

	if ( ! productTour ) {
		return;
	}

	gsap.registerPlugin( ScrollTrigger );

	const bg           = productTour.querySelector( '.v-product-tour__bg' );
	const columns      = productTour.querySelector( '.v-product-tour__columns' );
	const frame        = productTour.querySelector( '.v-product-tour__media' );
	const triggers     = productTour.querySelectorAll( '[data-trigger]' );
	const endTrigger   = productTour.querySelector( '.v-product-tour__section--end' );
	const firstVideo   = productTour.querySelector( '[data-media="0"] video' );
	const mobileVideos = productTour.querySelectorAll( '.v-product-tour__section-media video' );
	const query        = '(min-width: 1024px)';
	const gmedia       = gsap.matchMedia();

	/**
	 * Event lister to remove the playing class from the video
	 *
	 * @param {Event} evt The event
	 */
	const removePlayingClass = ( evt ) => {
		const { target } = evt;
		target.classList.remove( 'playing' );
	};

	/**
	 * Play the video when the section is in view.
	 *
	 * @param {HTMLMediaElement} video Video element to play.
	 * @param {boolean} restart Whether to restart the video.
	 */
	const playVideo = async ( video, restart = false ) => {
		try {
			if ( restart ) {
				video.currentTime = 0;
			}

			video.addEventListener( 'ended', removePlayingClass );
			video.addEventListener( 'pause', removePlayingClass );

			await video.play();
			video.classList.add( 'playing' );
		} catch ( err ) {
			video.classList.remove( 'playing' );
		}
	};

	// GSAP media queries.
	gmedia.add( query, () => {
		const header = document.querySelector( '.v-header' );

		gsap.to( frame, {
			scrollTrigger: {
				trigger: frame,
				start:   `center center+=${ header.getBoundingClientRect().height / 2 }`,
				end:     'bottom center+=10',
				endTrigger,
				pin:     true,
				pinType: 'transform',
				scrub:   .1,
			},
		} );

		const bgFade = gsap
			.timeline( {
				paused:   true,
				defaults: {
					duration: .3,
					ease:     'sine.out',
				},
			} )
			.to( bg, {
				opacity:    .03,
				onComplete: () => {
					bg.classList.remove( 'hidden' );
				},
				onReverseComplete: () => {
					bg.classList.add( 'hidden' );
				},
			} );

		ScrollTrigger.create( {
			trigger: columns,
			start:   'top bottom-=100%',
			onEnter: () => {
				bgFade.restart();
				playVideo( firstVideo );
			},
			onLeaveBack: () => {
				bgFade.reverse( 0 );
			},
		} );

		ScrollTrigger.create( {
			trigger: endTrigger,
			start:   'bottom bottom-=10%',
			onEnter: () => {
				bgFade.reverse( 0 );
			},
			onLeaveBack: () => {
				bgFade.restart();
			},
		} );
	} );

	[ ...triggers ].forEach( ( trigger ) => {
		const mediaIdx  = parseInt( trigger.getAttribute( 'data-trigger' ), 10 );

		if ( ! mediaIdx ) {
			return;
		}

		const nextMedia = productTour.querySelector( `[data-media="${ mediaIdx }"]` );
		const nextBadge = nextMedia.hasAttribute( 'data-timestamp' ) ? nextMedia.querySelector( '.v-product-tour__user-data' ) : null;
		const prevMedia = nextMedia.previousElementSibling;
		const prevBadge = prevMedia.hasAttribute( 'data-timestamp' ) ? prevMedia.querySelector( '.v-product-tour__user-data' ) : null;
		const section   = trigger.closest( '.v-product-tour__section' );

		/**
		 * Plays the video
		 *
		 * @param {HTMLElement} media Element with a video child
		 * @param {boolean}     restart  Whether to set the video to the beginning
		 */
		const playMedia = async ( media, restart = false ) => {
			const video = media.querySelector( 'video' );

			if ( ! video ) {
				return;
			}

			if ( ! video.classList.contains( 'loaded' ) ) {
				video.addEventListener( 'loadedmetadata', () => {
					playVideo( video, restart );
				} );

				return;
			}

			playVideo( video, restart );
		};

		// Scroll down timeline
		const fwdTl = gsap.timeline( {
			paused:   true,
			defaults: {
				duration: .3,
				ease:     'sine.out',
			},
			onComplete: () => {
				playMedia( nextMedia, true );
				nextMedia.removeAttribute( 'style' );
			},
		} )
			.to( prevMedia, {
				opacity:    0,
				onComplete: () => {
					prevMedia.classList.add( 'hidden' );
					prevMedia.querySelector( 'video' ).pause();

					if ( prevBadge ) {
						prevBadge.classList.add( 'hidden' );
						prevBadge.classList.remove( 'revealed' );
					}

					prevMedia.removeAttribute( 'style' );
				},
			} )
			.to( nextMedia, {
				opacity:  1,
				duration: .6,
				onStart:  () => {
					nextMedia.classList.remove( 'hidden' );

					if ( nextBadge ) {
						nextBadge.classList.add( 'hidden' );
						nextBadge.classList.remove( 'revealed' );
					}
				},
			}, '>' );

		// Scroll up timeline
		const rwdTl = gsap.timeline( {
			paused:   true,
			defaults: {
				duration: .3,
				ease:     'sine.out',
			},
		} )
			.to( nextMedia, {
				opacity:    0,
				onComplete: () => {
					nextMedia.classList.add( 'hidden' );
					nextMedia.removeAttribute( 'style' );
					nextMedia.querySelector( 'video' ).pause();
				},
			} )
			.to( prevMedia, {
				opacity:  1,
				duration: .6,
				onStart:  () => {
					prevMedia.classList.remove( 'hidden' );
					if ( prevBadge ) {
						prevBadge.classList.remove( 'revealed' );
						prevBadge.classList.add( 'hidden' );
						prevBadge.removeAttribute( 'style' );
					}

					playMedia( prevMedia, true );
				},
			}, '>' );

		if ( nextBadge || prevBadge ) {
			[ nextBadge, prevBadge ].forEach( ( badge ) => {
				if ( ! badge ) {
					return;
				}

				const parent    = badge.closest( '.v-product-tour__video-wrap' );
				const timestamp = parseFloat( parent.getAttribute( 'data-timestamp' ), 10 );
				const video     = parent.querySelector( 'video' );

				if (
					! timestamp ||
					( timestamp > video.duration || timestamp < 0 )
				) {
					return;
				}

				let animating    = false;
				const badgeTween = gsap.to( badge, {
					paused:   true,
					opacity:  1,
					duration: .3,
					ease:     'sine.out',
					onStart:  () => {
						animating = true;
						badge.classList.remove( 'hidden' );
					},
					onComplete: () => {
						badge.classList.add( 'revealed' );
						// badge.classList.add( 'hidden' );
						badge.removeAttribute( 'style' );
						animating = false;
					},
				} );

				video.addEventListener( 'timeupdate', ( e ) => {
					const { target } = e;
					const isRevealed = badge.classList.contains( 'revealed' );

					if ( animating || ( isRevealed && target.currentTime >= timestamp ) ) {
						return;
					}

					if ( target.currentTime >= timestamp ) {
						badgeTween.restart();
					}
				} );
			} );
		}

		gmedia.add( query, () => {
			ScrollTrigger.create( {
				trigger: section,
				start:   'top center',
				onEnter: () => {
					fwdTl.restart();
				},
				onLeaveBack: () => {
					rwdTl.restart();
				},
			} );
		} );
	} );

	[ ...mobileVideos ].forEach( ( video ) => {
		const parent    = video.closest( '.v-product-tour__section-media' );
		const userData  = parent.querySelector( '.v-product-tour__user-data' );
		const hasReveal = parent.hasAttribute( 'data-timestamp' );

		gmedia.add( '(max-width: 1023px)', () => {
			if ( video.classList.contains( 'v-lazy-video' ) ) {
				video.addEventListener( 'loadedmetadata', () => {
					// Video should play automatically on mobile
					playVideo( video );
				} );
			}

			let animating     = false;
			/**
			 * Resets the user badge to its initial state.
			 */
			const resetBadge = () => {
				if ( ! hasReveal || ! userData.classList.contains( 'revealed' ) ) {
					return;
				}

				userData.classList.remove( 'revealed' );
				userData.classList.add( 'hidden' );
				userData.removeAttribute( 'style' );
				animating = false;
			};

			const revealTween = gsap.to( userData, {
				paused:   true,
				opacity:  1,
				duration: .3,
				ease:     'sine.out',
				onStart:  () => {
					animating = true;
					userData.classList.remove( 'hidden' );
				},
				onComplete: () => {
					userData.classList.add( 'revealed' );
					userData.removeAttribute( 'style' );
					animating = false;
				},
				onReverseComplete: resetBadge,
			} );

			video.addEventListener( 'timeupdate', ( e ) => {
				// console.log( 'timeupdate', e.target );

				if ( ! hasReveal ) {
					return;
				}

				const { target } = e;
				const timestamp  = parseFloat( parent.getAttribute( 'data-timestamp' ), 10 );
				const isRevealed = userData.classList.contains( 'revealed' );

				if ( animating || ( isRevealed && target.currentTime >= timestamp ) ) {
					return;
				}

				if ( target.currentTime >= timestamp ) {
					revealTween.restart();
				}
			} );

			ScrollTrigger.create( {
				trigger: parent,
				start:   'top bottom-=10%',
				onEnter: () => {
					resetBadge();

					if ( ! video.classList.contains( 'playing' ) && video.classList.contains( 'loaded' ) ) {
						playVideo( video, true );
					}
				},
				onLeave: () => {
					if ( video.classList.contains( 'playing' ) ) {
						video.pause();
					}

					if ( hasReveal ) {
						animating = true;
						revealTween.reverse();
					}
				},
				onEnterBack: () => {
					resetBadge();

					if ( video.classList.contains( 'loaded' ) ) {
						playVideo( video, true );
					}
				},
				onLeaveBack: () => {
					if ( video.classList.contains( 'playing' ) ) {
						video.pause();
						// console.log( 'pause', video );
					}

					// resetBadge();
					if ( hasReveal ) {
						animating = true;
						revealTween.reverse();
					}
				},
			} );
		} );
	} );
};

export default vProductTour;
